import React from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

interface BackButtonBase {
  /** Read by screen readers and shown as the tooltip. */
  label?: string;
  /** `inverse` goes on dark surfaces. */
  tone?: 'default' | 'inverse';
  /** Shows `label` beside the arrow — «Назад к сессиям» above a detail view. */
  showLabel?: boolean;
  className?: string;
}

export type BackButtonProps = BackButtonBase &
  (
    | { to: string; state?: unknown; onClick?: never; disabled?: never }
    | { to?: never; state?: never; onClick: React.MouseEventHandler<HTMLButtonElement>; disabled?: boolean }
  );

const toneClasses = {
  default: 'text-ink hover:bg-surface-muted active:bg-line',
  inverse: 'text-white hover:bg-white/10 active:bg-white/20',
};

const Arrow = () => (
  <svg width="20" height="16" viewBox="0 0 27 22" fill="none" aria-hidden>
    <path
      d="M11 1.5L1.5 11M1.5 11L11 20.5M1.5 11H25.5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** The one "go back" control of the app: a link when it has a destination, a button when it runs a handler. */
export const BackButton = ({ label = 'Назад', tone = 'default', showLabel, className, ...rest }: BackButtonProps) => {
  const classes = cx(
    'inline-flex shrink-0 cursor-pointer items-center rounded-control transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
    'disabled:cursor-not-allowed disabled:opacity-50',
    showLabel
      ? 'min-h-11 touch-manipulation gap-2 px-2 text-label text-ink hover:bg-surface hover:text-primary'
      : ['size-8 justify-center', toneClasses[tone]],
    className,
  );
  const content = (
    <>
      <Arrow />
      {showLabel && label}
    </>
  );
  const a11y = showLabel ? {} : { 'aria-label': label, title: label };

  if (rest.to !== undefined) {
    return (
      <Link to={rest.to} state={rest.state} {...a11y} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" {...a11y} className={classes} onClick={rest.onClick} disabled={rest.disabled}>
      {content}
    </button>
  );
};
